/**
 * Accommodation date range picker built on React Aria Components RangeCalendar.
 *
 * Public API: ISO strings only. CalendarDate types stay inside this module.
 * Uses the shared CalendarCellContent cell renderer and calendarTheme CSS vars.
 *
 * Design: Compact Gold treatment from availability-calendar-mock.html (.t2 rules).
 * Behaviour:
 *   - allowsNonContiguousRanges is left unset (= false) by default: a range
 *     cannot span an unavailable night — matches the server validation rule.
 *     With anchorAwareUnavailability the predicate owns contiguity instead
 *     (react-stately's clamp would otherwise treat every below-minimum or
 *     departure-disallowed night as a wall and make later dates unselectable).
 *   - isIsoDateUnavailable: sparse-map reading — absent from predicate = selectable.
 *   - minIso / maxIso: caps selection to the available booking window.
 *   - commitBehavior: 'select' (default) commits the focused date when focus
 *     leaves the calendar; 'reset' discards it. A host that moves focus out
 *     of the calendar inside onRangeChange needs 'reset', or that blur
 *     overwrites the tapped end date with the stale focused date.
 */
import React, { useCallback, useContext, useEffect } from 'react';
import {
  Button,
  CalendarGrid,
  CalendarGridBody,
  CalendarGridHeader,
  CalendarHeaderCell,
  Heading,
  RangeCalendar,
  RangeCalendarStateContext,
} from 'react-aria-components';
import type { DateRange, RangeCalendarProps } from 'react-aria-components';

import { getLocalTimeZone } from '@internationalized/date';
import type { CalendarDate, DateDuration, DateValue } from '@internationalized/date';

import { SIZING } from '../../../styles/tokens';
import type { SurfaceTone } from '../../../styles/tokens';
import { CalendarCellContent } from './CalendarCellContent';
import './availabilityCalendar.css';
import { calendarDateToIso, parseIsoToCalendarDate } from './calendarDateAdapters';
import { calendarCreamThemeVars, calendarThemeVars } from './calendarTheme';

export interface AvailabilityRangeCalendarProps {
  /** ISO 'YYYY-MM-DD' check-in date, or '' for unset */
  startDate: string;
  /** ISO 'YYYY-MM-DD' check-out date, or '' for unset */
  endDate: string;
  /** Called when the user commits a range selection */
  onRangeChange: (startIso: string, endIso: string) => void;
  /**
   * Returns true when the given ISO date should be treated as unavailable.
   * Absent-from-predicate dates are selectable (sparse-map reading).
   * `anchorIso` is the first-tapped check-in while a range is half-picked,
   * otherwise null.
   */
  isIsoDateUnavailable: (iso: string, anchorIso: string | null) => boolean;
  /** ISO 'YYYY-MM-DD' — earliest selectable date (today in normal usage) */
  minIso: string;
  /** ISO 'YYYY-MM-DD' — latest selectable date (today + window in normal usage) */
  maxIso: string;
  /** Accessible label for the calendar */
  'aria-label'?: string;
  /** Number of adjacent months to display. Defaults to the existing single-month view. */
  visibleMonths?: 1 | 2;
  /** When true the predicate owns range contiguity and sees the first-tapped date. */
  anchorAwareUnavailability?: boolean;
  /** Reports the first-tapped check-in (ISO), or null once the range is committed or cancelled. */
  onAnchorChange?: (anchorIso: string | null) => void;
  /** The surface the calendar draws on; booking surfaces stay dark. */
  surface?: SurfaceTone;
  /** Drops the per-month grid heading, for a single month whose name the
   *  calendar header already shows. */
  hideGridMonthLabel?: boolean;
  /** What a blur does to a half-picked range; see the Behaviour note above. */
  commitBehavior?: RangeCalendarProps<DateValue>['commitBehavior'];
}

interface CalendarMonthGridProps {
  offset?: DateDuration;
  hideLabel?: boolean;
}

function CalendarMonthGrid({
  offset,
  hideLabel = false,
}: CalendarMonthGridProps): React.ReactElement | null {
  const calendarState = useContext(RangeCalendarStateContext);
  if (!calendarState) return null;

  const monthStart = offset
    ? calendarState.visibleRange.start.add(offset)
    : calendarState.visibleRange.start;
  const monthLabel = monthStart.toDate(getLocalTimeZone()).toLocaleDateString(undefined, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <section className="availability-calendar-month" aria-label={monthLabel}>
      {!hideLabel && <h3 className="availability-calendar-month__label">{monthLabel}</h3>}
      <CalendarGrid offset={offset}>
        <CalendarGridHeader>
          {(day) => <CalendarHeaderCell>{day}</CalendarHeaderCell>}
        </CalendarGridHeader>
        <CalendarGridBody>{(date) => <CalendarCellContent date={date} />}</CalendarGridBody>
      </CalendarGrid>
    </section>
  );
}

interface AnchorReporterProps {
  onAnchorChange: (anchorIso: string | null) => void;
}

/** Reports the half-picked range's first-tapped date to the host; null when it clears or unmounts. */
function AnchorReporter({ onAnchorChange }: AnchorReporterProps): null {
  const calendarState = useContext(RangeCalendarStateContext);
  const anchorIso = calendarState?.anchorDate
    ? calendarDateToIso(calendarState.anchorDate as CalendarDate)
    : null;

  useEffect(() => {
    onAnchorChange(anchorIso);
  }, [anchorIso, onAnchorChange]);

  useEffect(() => () => onAnchorChange(null), [onAnchorChange]);

  return null;
}

/**
 * Range calendar for accommodation date selection.
 * Applies the Compact Gold visual treatment from the design mock.
 */
export function AvailabilityRangeCalendar({
  startDate,
  endDate,
  onRangeChange,
  isIsoDateUnavailable,
  minIso,
  maxIso,
  'aria-label': ariaLabel = 'Check-in and check-out dates',
  visibleMonths = 1,
  anchorAwareUnavailability = false,
  onAnchorChange,
  surface = 'dark',
  hideGridMonthLabel = false,
  commitBehavior = 'select',
}: AvailabilityRangeCalendarProps): React.ReactElement {
  const parsedValue: DateRange | null =
    startDate && endDate
      ? {
          start: parseIsoToCalendarDate(startDate),
          end: parseIsoToCalendarDate(endDate),
        }
      : null;

  const parsedMin = parseIsoToCalendarDate(minIso);
  const parsedMax = parseIsoToCalendarDate(maxIso);

  const handleChange = useCallback(
    (range: DateRange | null) => {
      if (range) {
        onRangeChange(
          calendarDateToIso(range.start as CalendarDate),
          calendarDateToIso(range.end as CalendarDate),
        );
      }
    },
    [onRangeChange],
  );

  const isDateUnavailableForRac = useCallback(
    (date: DateValue, anchorDate: DateValue | null): boolean =>
      isIsoDateUnavailable(
        calendarDateToIso(date as CalendarDate),
        anchorDate ? calendarDateToIso(anchorDate as CalendarDate) : null,
      ),
    [isIsoDateUnavailable],
  );

  return (
    <RangeCalendar
      aria-label={ariaLabel}
      value={parsedValue}
      onChange={handleChange}
      commitBehavior={commitBehavior}
      minValue={parsedMin}
      maxValue={parsedMax}
      isDateUnavailable={isDateUnavailableForRac}
      visibleDuration={{ months: visibleMonths }}
      // Default (flag off): react-stately clamps ranges around unavailable
      // dates, matching the server-side validateAvailabilitySelection rule.
      // Flag on: the predicate already refuses every date past a blocked
      // night, so the clamp is switched off.
      allowsNonContiguousRanges={anchorAwareUnavailability}
      className="availability-calendar"
      style={
        {
          ...(surface === 'cream' ? calendarCreamThemeVars : calendarThemeVars),
          '--cal-two-month-gap': SIZING.calendarTwoMonthGap,
          '--cal-month-label-margin-bottom': SIZING.calendarMonthLabelMarginBottom,
          '--cal-month-label-font-size': SIZING.calendarMonthLabelFontSize,
          '--cal-overlay-header-margin-bottom': SIZING.calendarOverlayHeaderMarginBottom,
        } as React.CSSProperties
      }
    >
      {onAnchorChange && <AnchorReporter onAnchorChange={onAnchorChange} />}
      <header className="availability-calendar-header">
        <Button slot="previous">‹</Button>
        <Heading />
        <Button slot="next">›</Button>
      </header>
      <div
        className={
          visibleMonths === 2
            ? 'availability-calendar-months availability-calendar-months--two'
            : 'availability-calendar-months'
        }
      >
        <CalendarMonthGrid hideLabel={hideGridMonthLabel} />
        {visibleMonths === 2 && <CalendarMonthGrid offset={{ months: 1 }} />}
      </div>
    </RangeCalendar>
  );
}
